import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheck, FiHeart, FiMinus, FiPlus, FiX } from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

function ProductDetail() {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, wishlistItems, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [quantity, setQuantity] = useState(1);
  const [sizeError, setSizeError] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    document.title = product
      ? `${product.name} | ${siteConfig.displayName}`
      : `Product not found | ${siteConfig.displayName}`;
  }, [product]);

  useEffect(() => {
    setSelectedSize("");
    setSelectedColor(product?.colors?.[0] || "");
    setQuantity(1);
    setAdded(false);
    setSizeError(false);
    setSizeGuideOpen(false);
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.category === product.category && item.id !== product.id
    );
    return [...sameCategory, ...productData.filter((item) => item.id !== product.id)]
      .filter((item, index, items) => items.findIndex((p) => p.id === item.id) === index)
      .slice(0, 8);
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found">
        <span>404</span>
        <h1>Product not found</h1>
        <p>The piece may have moved, but the collection is waiting.</p>
        <Link className="button" to="/shop">Back to shop</Link>
      </main>
    );
  }

  const isWishlisted = wishlistItems.includes(product.id);

  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true);
      document.querySelector(".size-selector")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setSizeError(false);
    setAdded(true);
  };

  const renderProductRail = (title, items) => (
    <section className="related-products">
      <h2>{title}</h2>
      <div>
        {items.map((item) => (
          <article key={item.id}>
            <Link to={`/shopDetails/${item.id}`}>
              <img src={item.image} alt={item.name} loading="lazy" />
              <strong>{item.name}</strong>
              <span>{formatPrice(item.price)}</span>
            </Link>
          </article>
        ))}
      </div>
    </section>
  );

  return (
    <main className="product-detail">
      <div className="product-detail__layout">
        <section className="product-gallery" aria-label="Product images">
          {product.images.map((image, index) => (
            <figure key={image}>
              <img
                src={image}
                alt={index === 0 ? product.name : `${product.name} detail`}
                fetchpriority={index === 0 ? "high" : "auto"}
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
              />
              <span>{index + 1} / {product.images.length}</span>
            </figure>
          ))}
        </section>

        <aside className="product-info">
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <Link to="/shop?sort=new">New in</Link>
            <span>/</span>
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
              {product.category}
            </Link>
          </nav>

          <div className="product-title-row">
            <h1>{product.name}</h1>
            <span>{formatPrice(product.price)}</span>
          </div>

          <section className="color-selector" aria-labelledby="color-heading">
            <div className="option-heading" id="color-heading">
              Color <strong>{selectedColor}</strong>
            </div>
            <div className="color-options">
              {product.colors.map((color, index) => (
                <button
                  key={color}
                  className={selectedColor === color ? "is-selected" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-pressed={selectedColor === color}
                >
                  <span className={`color-chip color-chip--${(product.id + index) % 6}`} />
                  {color}
                </button>
              ))}
            </div>
          </section>

          <section className={`size-selector ${sizeError ? "has-error" : ""}`}>
            <div className="option-heading">
              <span>Size & fit</span>
              <button type="button" onClick={() => setSizeGuideOpen((open) => !open)}>
                Size guide
              </button>
            </div>
            {sizeGuideOpen && (
              <div className="size-guide" role="region" aria-label="Size conversion guide">
                <strong>EU</strong><span>32</span><span>34</span><span>36</span><span>38</span><span>40</span><span>42</span>
                <strong>US</strong><span>0</span><span>2</span><span>4</span><span>6</span><span>8</span><span>10</span>
              </div>
            )}
            <div className="size-options" role="group" aria-label="Select size">
              {product.size.map((size) => (
                <button
                  key={size}
                  className={selectedSize === size ? "is-selected" : ""}
                  onClick={() => {
                    setSelectedSize(size);
                    setSizeError(false);
                  }}
                  aria-pressed={selectedSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
            {sizeError && <p role="alert">Please select a size.</p>}
          </section>

          <div className="purchase-row">
            <div className="quantity-selector" aria-label="Quantity">
              <button
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                aria-label="Decrease quantity"
              >
                <FiMinus />
              </button>
              <span>{quantity}</span>
              <button
                onClick={() => setQuantity((value) => Math.min(10, value + 1))}
                aria-label="Increase quantity"
              >
                <FiPlus />
              </button>
            </div>
            <button className="button add-to-bag" onClick={handleAddToCart}>
              Add to cart
            </button>
          </div>

          <button
            className={`product-wishlist ${isWishlisted ? "is-active" : ""}`}
            onClick={() => toggleWishlist(product.id)}
          >
            <FiHeart /> {isWishlisted ? "Saved to wishlist" : "Add to wishlist"}
          </button>

          <div className="product-accordions">
            <details open>
              <summary>Product details <FiPlus /></summary>
              <div>
                <dl>
                  <dt>REFERENCE</dt>
                  <dd>{product.attributes.reference}</dd>
                  <dt>DESCRIPTION</dt>
                  <dd>{product.description[0]}</dd>
                </dl>
                <ul>{product.Features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                <h3>Fabric</h3>
                <p>{product.material}</p>
                <h3>Washing instructions</h3>
                <p>{product.attributes.care}</p>
              </div>
            </details>
            <details>
              <summary>Shipping & returns <FiPlus /></summary>
              <div>
                <p>Tracked delivery in 6–12 business days.</p>
                <p>
                  Complimentary shipping over {formatPrice(siteConfig.freeShippingThreshold)}.
                  Free returns within 30 days.
                </p>
                <Link to="/shipping">Read shipping information</Link>
              </div>
            </details>
            <details>
              <summary>Need help? <FiPlus /></summary>
              <div>
                <p>Our online customer care team is happy to help with sizing and styling.</p>
                <Link to="/contact">Contact us</Link>
              </div>
            </details>
          </div>
        </aside>
      </div>

      {renderProductRail("Complete the look", relatedProducts.slice(0, 4))}
      {renderProductRail("Related products", relatedProducts.slice(4, 8))}

      <div
        className={`mini-cart-overlay ${added ? "is-open" : ""}`}
        onClick={() => setAdded(false)}
        aria-hidden="true"
      />
      <aside className={`mini-cart ${added ? "is-open" : ""}`} aria-live="polite">
        <header>
          <strong>Added to your cart</strong>
          <button onClick={() => setAdded(false)} aria-label="Close cart"><FiX /></button>
        </header>
        <div className="mini-cart__item">
          <img src={product.image} alt="" />
          <div>
            <FiCheck />
            <strong>{product.name}</strong>
            <span>{selectedColor} · {selectedSize} · Qty {quantity}</span>
            <span>{formatPrice(Number(product.price) * quantity)}</span>
          </div>
        </div>
        <footer>
          <Link className="button" to="/cartpage">View cart</Link>
          <button onClick={() => setAdded(false)}>Continue shopping</button>
        </footer>
      </aside>
    </main>
  );
}

export default ProductDetail;
